'use client';

import { useCallback, useRef, useState } from 'react';

import type { Chapter45Anchor } from '../../../chapter45';

interface UseHighlightNavigationOptions {
  /** Total number of quotes (source of truth from quote.texts) */
  quoteCount: number;
  /** Chapter 4.5 anchors */
  anchors: Chapter45Anchor[];
}

interface UseHighlightNavigationReturn {
  /** Current active quote index */
  activeQuoteIndex: number;
  /** Number of visible quotes (may differ from quoteCount if some aren't anchored) */
  visibleQuoteCount: number;
  /** Ref for the active mark element */
  activeMarkRef: React.MutableRefObject<HTMLElement | null>;
  /** Focus on a specific quote index */
  focusQuoteAtIndex: (idx: number) => boolean;
  /** Scroll to the first highlight */
  scrollToFirstHighlight: () => boolean;
  /** Whether we've already scrolled to the first highlight */
  hasScrolledToHighlight: React.MutableRefObject<boolean>;
}

/**
 * Hook for managing highlight navigation in the ChPL viewer.
 *
 * Provides functionality for:
 * - Tracking the active quote index
 * - Navigating between quotes
 * - Scrolling to highlights
 */
export function useHighlightNavigation(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  options: UseHighlightNavigationOptions,
): UseHighlightNavigationReturn {
  const { quoteCount, anchors } = options;
  const [activeQuoteIndex, setActiveQuoteIndex] = useState(0);
  const [visibleQuoteCount, setVisibleQuoteCount] = useState(0);
  const activeMarkRef = useRef<HTMLElement | null>(null);
  const hasScrolledToHighlight = useRef(false);

  /**
   * Group marks by quote index.
   * Quotes are already sorted by document position on the server.
   */
  const getMarksByQuote = useCallback((): HTMLElement[][] => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || quoteCount === 0) return [];

    const marks = Array.from(
      scrollContainer.querySelectorAll('mark'),
    ) as HTMLElement[];
    if (marks.length === 0) return [];

    const firstMarkHasQuoteIndex = marks[0]?.dataset.quoteIndex !== undefined;

    if (firstMarkHasQuoteIndex) {
      const groups: HTMLElement[][] = Array.from(
        { length: quoteCount },
        () => [],
      );
      for (const m of marks) {
        const qi = parseInt(m.dataset.quoteIndex ?? '-1', 10);
        if (qi >= 0 && qi < quoteCount) {
          groups[qi]!.push(m);
        }
      }
      return groups.filter((g) => g.length > 0);
    } else {
      const groups: HTMLElement[][] = Array.from(
        { length: quoteCount },
        () => [],
      );
      const marksPerQuote = Math.ceil(marks.length / quoteCount);
      for (let i = 0; i < marks.length; i++) {
        const groupIdx = Math.min(
          Math.floor(i / marksPerQuote),
          quoteCount - 1,
        );
        groups[groupIdx]!.push(marks[i]!);
      }
      return groups.filter((g) => g.length > 0);
    }
  }, [quoteCount, scrollContainerRef]);

  const focusQuoteAtIndex = useCallback(
    (idx: number): boolean => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return false;

      const groups = getMarksByQuote();
      if (groups.length === 0) return false;

      setVisibleQuoteCount(groups.length);

      const clamped = Math.max(0, Math.min(groups.length - 1, idx));
      const targetGroup = groups[clamped];
      if (!targetGroup || targetGroup.length === 0) return false;

      const el = targetGroup[0]!;

      // Remove active state from all marks
      const allMarks = Array.from(
        scrollContainer.querySelectorAll('mark[data-ch45-quote-mark]'),
      ) as HTMLElement[];
      allMarks.forEach((m) => (m.dataset.active = 'false'));

      // Add active state to all marks of current quote
      targetGroup.forEach((m) => (m.dataset.active = 'true'));
      activeMarkRef.current = el;

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setActiveQuoteIndex(clamped);
      return true;
    },
    [getMarksByQuote, scrollContainerRef],
  );

  const scrollToFirstHighlight = useCallback((): boolean => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return false;

    const firstHighlight = scrollContainer.querySelector('mark');
    if (firstHighlight) {
      return focusQuoteAtIndex(0);
    }

    // Fallback: scroll to the first anchored block if we have anchors
    const firstAnchor = anchors[0];
    if (firstAnchor) {
      const target = scrollContainer.querySelector(
        `[data-ch45-blockid="${CSS.escape(firstAnchor.blockId)}"]`,
      );

      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      }
    }

    return false;
  }, [anchors, focusQuoteAtIndex, scrollContainerRef]);

  return {
    activeQuoteIndex,
    visibleQuoteCount,
    activeMarkRef,
    focusQuoteAtIndex,
    scrollToFirstHighlight,
    hasScrolledToHighlight,
  };
}
